import {
  createContext, useContext, useState, useCallback, useMemo, ReactNode,
} from "react";
import { useSelector } from "react-redux";
import { api } from "../../../server/request";
import { DEFAULT_ALERT_CONFIG, type AlertConfig } from "../utils/alertUtils";

export interface TrendRow {
  date: string; label: string;
  totalWAN: number; authUsage: number; staticUsage: number; unauthUsage: number;
  captiveUsers: number; authPerUser: number;
}
export interface HeatmapSite {
  site: string;
  values: { day: string; value: number | null }[];
}
export interface RawRow {
  date: string; label: string; site: string; captiveUsers: number;
  wan: number; auth: number; staticDevices: number; unauth: number; authPerUser: number;
}
export interface SummaryRow {
  date: string; site: string; captiveUsers: number;
  wan: number; auth: number; staticDevices: number; unauth: number;
  authPerUser: number; unauthPct: number;
}
export interface SiteAggregate {
  site: string; totalWan: number; totalAuth: number; totalStatic: number;
  totalUnauth: number; avgUsers: number; avgAuthPerUser: number | null; days: number;
}
export interface DashboardData {
  trendData: TrendRow[];
  heatmapData: HeatmapSite[];
  heatmapHeaders: string[];
  rawData: RawRow[];
  summaries: Record<string, SummaryRow[]>;
  siteAggregates: SiteAggregate[];
  period: string;
  fileName?: string;
}

function excelSerialToISO(serial: number): string {
  const d = new Date((serial - 25569) * 86400 * 1000);
  return d.toISOString().split("T")[0];
}

function isoToLabel(iso: string): string {
  const [, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${parseInt(d)}-${months[parseInt(m) - 1]}`;
}

function toISO(val: unknown): string {
  if (typeof val === "number") return excelSerialToISO(val);
  const s = String(val).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const slashParts = s.split("/");
  if (slashParts.length === 3) {
    const [a, b, y] = slashParts;
    if (y.length === 4) {
      const month = a.length <= 2 && parseInt(a) <= 12 ? a.padStart(2,"0") : b.padStart(2,"0");
      const day   = a.length <= 2 && parseInt(a) <= 12 ? b.padStart(2,"0") : a.padStart(2,"0");
      return `${y}-${month}-${day}`;
    }
  }
  const d = new Date(s);
  if (!isNaN(d.getTime())) return d.toISOString().split("T")[0];
  return s;
}

function round2(n: number): number { return Math.round(n * 100) / 100; }

export function computePeriod(trendData: TrendRow[]): string {
  if (!trendData.length) return "";
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const parse = (iso: string) => {
    const [y, m, d] = iso.split("-").map(Number);
    return { y, m, d };
  };
  const first = parse(trendData[0].date);
  const last  = parse(trendData[trendData.length - 1].date);
  if (first.m === last.m && first.y === last.y) {
    return `${months[first.m - 1]} ${first.d}–${last.d}, ${first.y}`;
  }
  return `${months[first.m - 1]} ${first.d} – ${months[last.m - 1]} ${last.d}, ${last.y}`;
}

function parseRawRows(rows: unknown[][]): RawRow[] {
  const dataRows = rows.slice(1).filter(r => r[0] != null && r[1] != null && String(r[1]).trim() !== "");

  return dataRows.map(r => {
    const iso = toISO(r[0]);
    return {
      date: iso, label: isoToLabel(iso),
      site: String(r[1]).trim(),
      captiveUsers: Number(r[2]) || 0,
      wan:          round2(Number(r[3]) || 0),
      auth:         round2(Number(r[4]) || 0),
      staticDevices:round2(Number(r[5]) || 0),
      unauth:       round2(Number(r[6]) || 0),
      authPerUser:  round2(Number(r[7]) || 0),
    };
  });
}

function buildDashboardDataFromRawRows(rawData: RawRow[]): Omit<DashboardData, "period" | "fileName"> {
  const seenDates = new Set<string>();
  const dates: string[] = [];
  rawData.forEach(r => { if (!seenDates.has(r.date)) { seenDates.add(r.date); dates.push(r.date); } });
  dates.sort();
  const labelOf = Object.fromEntries(dates.map(d => [d, isoToLabel(d)]));
  const heatmapHeaders = dates.map(d => labelOf[d]);

  const trendAcc: Record<string, { wan:number; auth:number; static:number; unauth:number; users:number; apuSum:number; n:number }> = {};
  rawData.forEach(r => {
    if (!trendAcc[r.date]) trendAcc[r.date] = { wan:0, auth:0, static:0, unauth:0, users:0, apuSum:0, n:0 };
    const t = trendAcc[r.date];
    t.wan += r.wan; t.auth += r.auth; t.static += r.staticDevices;
    t.unauth += r.unauth; t.users += r.captiveUsers; t.apuSum += r.authPerUser; t.n++;
  });
  const trendData: TrendRow[] = dates.map(d => ({
    date: d, label: labelOf[d],
    totalWAN:    round2(trendAcc[d].wan),
    authUsage:   round2(trendAcc[d].auth),
    staticUsage: round2(trendAcc[d].static),
    unauthUsage: round2(trendAcc[d].unauth),
    captiveUsers: trendAcc[d].users,
    authPerUser: round2(trendAcc[d].apuSum / trendAcc[d].n),
  }));

  const siteDateMap: Record<string, Record<string, number | null>> = {};
  rawData.forEach(r => {
    if (!siteDateMap[r.site]) siteDateMap[r.site] = {};
    siteDateMap[r.site][r.date] = r.authPerUser;
  });
  const sites = Array.from(new Set(rawData.map(r => r.site)));
  const heatmapData: HeatmapSite[] = sites.map(site => ({
    site,
    values: dates.map(d => ({ day: labelOf[d], value: siteDateMap[site]?.[d] ?? null })),
  }));
  heatmapData.sort((a, b) =>
    (b.values[b.values.length - 1].value ?? 0) - (a.values[a.values.length - 1].value ?? 0)
  );

  const summaries: Record<string, SummaryRow[]> = {};
  dates.forEach(d => {
    const label = labelOf[d];
    summaries[label] = rawData
      .filter(r => r.date === d)
      .map(r => ({
        date: r.date, site: r.site, captiveUsers: r.captiveUsers,
        wan: r.wan, auth: r.auth, staticDevices: r.staticDevices, unauth: r.unauth,
        authPerUser: r.authPerUser,
        unauthPct: r.wan > 0 ? round2((r.unauth / r.wan) * 100) : 0,
      }))
      .sort((a, b) => b.wan - a.wan);
  });

  const aggMap: Record<string, { wan:number; auth:number; static:number; unauth:number; users:number; days:number }> = {};
  rawData.forEach(r => {
    if (!aggMap[r.site]) aggMap[r.site] = { wan:0, auth:0, static:0, unauth:0, users:0, days:0 };
    const a = aggMap[r.site];
    a.wan += r.wan; a.auth += r.auth; a.static += r.staticDevices;
    a.unauth += r.unauth; a.users += r.captiveUsers; a.days++;
  });
  const siteAggregates: SiteAggregate[] = Object.entries(aggMap)
    .map(([site, a]) => ({
      site,
      totalWan:   round2(a.wan),
      totalAuth:  round2(a.auth),
      totalStatic:round2(a.static),
      totalUnauth:round2(a.unauth),
      avgUsers:   round2(a.users / a.days),
      avgAuthPerUser: a.users > 0 ? round2(a.auth / a.users) : null,
      days: a.days,
    }))
    .sort((a, b) => b.totalWan - a.totalWan);

  return { trendData, heatmapData, heatmapHeaders, rawData, summaries, siteAggregates };
}

async function parseWorkbookToDashboardData(buf: ArrayBuffer, fileName: string): Promise<DashboardData> {
  const XLSX = await import("xlsx");
  const wb = XLSX.read(buf, { type: "array" });
  const preferredNames = ["Data", "Sheet1", "data", "sheet1"];
  const sheetName = preferredNames.find(n => wb.SheetNames.includes(n)) ?? wb.SheetNames[0];
  const ws = wb.Sheets[sheetName];
  const rows: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown[][];
  if (rows.length < 2 || (rows[0] as unknown[]).length < 7) {
    throw new Error("File doesn't look like a fleet data sheet (need Date, Site, Users, WAN, Auth, Static, UnAuth columns).");
  }
  const rawData = parseRawRows(rows);
  const processed = buildDashboardDataFromRawRows(rawData);
  const period = computePeriod(processed.trendData);
  return { ...processed, period, fileName };
}

interface DataCtx {
  data: DashboardData | null;
  hasData: boolean;
  period: string;
  thresholds: AlertConfig;
  isLoading: boolean;
  error: string | null;
  notFoundForDate: string | null;
  loadFile: (file: File) => Promise<void>;
  loadFromDate: (date: string) => Promise<void>;
  clearData: () => void;
  setThresholds: (cfg: AlertConfig) => void;
}

const Ctx = createContext<DataCtx | null>(null);

export function useData(): DashboardData {
  const c = useContext(Ctx);
  if (!c) throw new Error("useData must be used inside DataProvider");
  if (!c.data) throw new Error("useData called before a file was uploaded — check hasData first");
  return c.data;
}

export function useDataCtx(): DataCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useDataCtx must be used inside DataProvider");
  return c;
}

export function DataProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<DashboardData | null>(null);
  const [thresholds, setThresholdsState] = useState<AlertConfig>(DEFAULT_ALERT_CONFIG);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notFoundForDate, setNotFoundForDate] = useState<string | null>(null);
  const dpId = useSelector((state: any) => state.authReducer?.selectedOu?.id);

  const period = useMemo(() => (data ? computePeriod(data.trendData) : ""), [data]);

  const loadFile = useCallback(async (file: File) => {
    setIsLoading(true);
    setError(null);
    setNotFoundForDate(null);
    try {
      const buf = await file.arrayBuffer();
      const newData = await parseWorkbookToDashboardData(buf, file.name);
      setData(newData);
    } catch (err: any) {
      setData(null);
      setError(err?.message ?? "Failed to parse file");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadFromDate = useCallback(async (date: string) => {
    setIsLoading(true);
    setError(null);
    setNotFoundForDate(null);
    try {
      if (!dpId) {
        setError("No organization selected — pick an organization before loading a report by date.");
        return;
      }
      const reportId = `${dpId}-${date}`;
      const res = await api.siteController.getBlob(`/v1/organizations/${dpId}/reports/${reportId}/download-direct?type=data-navigator`);
      const buf = await res.data.arrayBuffer();
      const newData = await parseWorkbookToDashboardData(buf, `${date}.xlsx`);
      setData(newData);
    } catch (err: any) {
      setData(null);
      if (err?.response?.status === 404) {
        setNotFoundForDate(date);
      } else {
        setError(err?.message ?? "Failed to load report for the selected date");
      }
    } finally {
      setIsLoading(false);
    }
  }, [dpId]);

  const clearData = useCallback(() => {
    setData(null);
    setError(null);
    setNotFoundForDate(null);
  }, []);

  const setThresholds = useCallback((cfg: AlertConfig) => {
    setThresholdsState(cfg);
  }, []);

  return (
    <Ctx.Provider value={{ data, hasData: !!data, period, thresholds, isLoading, error, notFoundForDate, loadFile, loadFromDate, clearData, setThresholds }}>
      {children}
    </Ctx.Provider>
  );
}
